/**
 * CodableLibrary — shared playbook vault (layer B).
 * Only Design System components (/core/design). Same chrome as Wiki.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery } from '@tanstack/react-query';
import Markdown from 'react-markdown';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  CircleDot,
  Copy,
  FileText,
  Filter,
  LayoutTemplate,
  Lightbulb,
  Loader2,
  MessageSquareCode,
  Search,
  ShieldAlert,
  Workflow,
} from 'lucide-react';
import { CodableIcon } from '../../../components/icons/CodableIcon';
import { toast } from 'sonner';
import { api } from '../../../lib/api';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  PageHeader,
  SearchInput,
  Toggle,
} from '../../../components/ui';
import { TemplatesCatalog } from './CodableTemplates';

type Kind =
  | ''
  | 'recipe'
  | 'prompt'
  | 'anti-pattern'
  | 'how-to'
  | 'reference'
  | 'policy'
  | 'glossary'
  | 'source';

interface LibraryHit {
  path: string;
  title: string;
  score: number;
  preview?: string;
  kind?: string;
  lifecycle?: string;
  tags?: string[];
}

interface SearchResp {
  ok: boolean;
  query?: string;
  kb_id?: string;
  lifecycle_filter?: string;
  total?: number;
  results?: LibraryHit[];
  note?: string;
  error?: string;
  browse?: boolean;
}

interface ReadResp {
  ok: boolean;
  path?: string;
  content?: string;
  frontmatter?: Record<string, unknown>;
  title?: string;
  error?: string;
}

function getKinds(t: TFunction) {
  return [
    { value: '' as Kind, label: t('codable.libraryAll', 'Vše'), icon: BookOpen, hint: t('codable.libraryHintAll', 'Celá knihovna') },
    { value: 'recipe' as Kind, label: t('codable.libraryRecipes', 'Recepty'), icon: Lightbulb, hint: t('codable.libraryHintRecipes', 'Ověřené postupy') },
    { value: 'prompt' as Kind, label: t('codable.libraryPrompts', 'Prompty'), icon: MessageSquareCode, hint: t('codable.libraryHintPrompts', 'Šablony promptů') },
    { value: 'anti-pattern' as Kind, label: t('codable.libraryAnti', 'Anti-patterns'), icon: ShieldAlert, hint: t('codable.libraryHintAnti', 'Co nedělat') },
    { value: 'how-to' as Kind, label: t('codable.libraryHowto', 'How-to'), icon: Workflow, hint: t('codable.libraryHintHowto', 'Krok za krokem') },
    { value: 'reference' as Kind, label: t('codable.libraryReference', 'Reference'), icon: FileText, hint: t('codable.libraryHintRef', 'Kontext a odkazy') },
    { value: 'policy' as Kind, label: t('codable.libraryPolicy', 'Policy'), icon: CircleDot, hint: t('codable.libraryHintPolicy', 'Pravidla') },
  ];
}

function kindMeta(kind?: string): {
  variant: 'blue' | 'purple' | 'red' | 'green' | 'gray' | 'yellow';
  label: string;
  ring: string;
  iconBg: string;
  Icon: React.ComponentType<{ size?: number | string; className?: string }>;
} {
  switch (kind) {
    case 'recipe':
      return {
        variant: 'blue',
        label: 'recipe',
        ring: 'ring-blue-500/30',
        iconBg: 'bg-blue-500/15 text-blue-300',
        Icon: Lightbulb,
      };
    case 'prompt':
      return {
        variant: 'purple',
        label: 'prompt',
        ring: 'ring-purple-500/30',
        iconBg: 'bg-purple-500/15 text-purple-300',
        Icon: MessageSquareCode,
      };
    case 'anti-pattern':
      return {
        variant: 'red',
        label: 'anti-pattern',
        ring: 'ring-red-500/30',
        iconBg: 'bg-red-500/15 text-red-300',
        Icon: ShieldAlert,
      };
    case 'how-to':
      return {
        variant: 'green',
        label: 'how-to',
        ring: 'ring-emerald-500/30',
        iconBg: 'bg-emerald-500/15 text-emerald-300',
        Icon: Workflow,
      };
    case 'draft':
      return {
        variant: 'yellow',
        label: 'draft',
        ring: 'ring-amber-500/30',
        iconBg: 'bg-amber-500/15 text-amber-300',
        Icon: CircleDot,
      };
    case 'active':
      return {
        variant: 'green',
        label: 'active',
        ring: 'ring-emerald-500/20',
        iconBg: 'bg-emerald-500/10 text-emerald-300',
        Icon: CheckCircle2,
      };
    default:
      return {
        variant: 'gray',
        label: kind || 'note',
        ring: 'ring-gray-700/40',
        iconBg: 'bg-gray-800 text-gray-400',
        Icon: FileText,
      };
  }
}

function folderOf(path: string): string {
  const i = path.lastIndexOf('/');
  return i > 0 ? path.slice(0, i) : '';
}

type HubTab = 'recipes' | 'templates';

/** `embedded` = záložka ModuleWorkspace (bez PageHeader / back na seznam). */
export function CodableLibrary({ embedded = false }: { embedded?: boolean } = {}) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const hubTab: HubTab = searchParams.get('tab') === 'templates' ? 'templates' : 'recipes';
  const setHubTab = (tab: HubTab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === 'recipes') next.delete('tab');
    else next.set('tab', 'templates');
    setSearchParams(next, { replace: true });
  };

  const KINDS = getKinds(t);
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [kind, setKind] = useState<Kind>('');
  const [includeDrafts, setIncludeDrafts] = useState(true);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);

  const searchQ = useQuery({
    queryKey: ['codable-library-search', submitted, kind, includeDrafts],
    queryFn: () =>
      api.mcp<SearchResp>('codable.library_search', {
        query: submitted.trim(),
        ...(kind ? { kind } : {}),
        include_drafts: includeDrafts,
        limit: 40,
      }),
    staleTime: 15_000,
  });

  const readQ = useQuery({
    queryKey: ['codable-library-read', selectedPath],
    queryFn: () => api.mcp<ReadResp>('codable.library_read', { path: selectedPath! }),
    enabled: Boolean(selectedPath),
  });

  const results = searchQ.data?.results ?? [];

  // Auto-select first result when list changes and nothing selected
  useEffect(() => {
    if (!selectedPath && results.length > 0) {
      setSelectedPath(results[0]!.path);
    }
  }, [results, selectedPath]);

  const selected = useMemo(
    () => results.find((r) => r.path === selectedPath) ?? null,
    [results, selectedPath],
  );

  const kindCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of results) {
      const k = r.kind || 'other';
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  }, [results]);

  function runSearch(e?: React.FormEvent) {
    e?.preventDefault();
    setSubmitted(query.trim());
    setSelectedPath(null);
  }

  async function copyPath(path: string) {
    try {
      await navigator.clipboard.writeText(path);
      toast.success(t('codable.libraryPathCopied'));
    } catch {
      toast.error(t('codable.libraryClipboardFail'));
    }
  }

  const browsing = !submitted;
  const selMeta = kindMeta(selected?.kind || (readQ.data?.frontmatter?.kind as string | undefined));

  return (
    <div className={embedded ? 'space-y-4 p-2' : 'space-y-5 p-6'}>
      {!embedded && (
        <PageHeader
          title={t('codable.libraryHubTitle')}
          description={t('codable.libraryHubDesc')}
          icon={hubTab === 'templates' ? LayoutTemplate : BookOpen}
          actions={
            <>
              <Link to='/modules/codable'>
                <Button variant='ghost' size='sm' icon={<ArrowLeft size={14} />}>
                  {t('codable.libraryProjects')}
                </Button>
              </Link>
              <Link to='/modules/codable/new'>
                <Button variant='primary' size='sm' icon={<CodableIcon size={14} />}>
                  {t('codable.newProject')}
                </Button>
              </Link>
            </>
          }
        />
      )}

      <div className='flex flex-wrap items-center gap-1.5'>
        <Button
          type='button'
          size='sm'
          variant={hubTab === 'recipes' ? 'primary' : 'secondary'}
          icon={<BookOpen size={13} />}
          onClick={() => setHubTab('recipes')}
        >
          {t('codable.libraryTabRecipes')}
        </Button>
        <Button
          type='button'
          size='sm'
          variant={hubTab === 'templates' ? 'primary' : 'secondary'}
          icon={<LayoutTemplate size={13} />}
          onClick={() => setHubTab('templates')}
        >
          {t('codable.libraryTabTemplates')}
        </Button>
      </div>

      {hubTab === 'templates' && <TemplatesCatalog embedded />}

      {hubTab === 'recipes' && (
        <>
        <Card>
          <CardContent className='space-y-4'>
            <form onSubmit={runSearch} className='flex flex-col gap-3 sm:flex-row sm:items-center'>
              <div className='min-w-0 flex-1'>
                <SearchInput
                  value={query}
                  onChange={setQuery}
                  size='md'
                  placeholder={t('codable.librarySearchPh')}
                  className='w-full'
                  resultCount={searchQ.isFetching ? undefined : results.length}
                  autoFocus
                />
              </div>
              <div className='flex items-center gap-2 shrink-0'>
                <Button type='submit' variant='primary' icon={<Search size={14} />}>
                  {t('codable.librarySearch')}
                </Button>
                {submitted && (
                  <Button
                    type='button'
                    variant='ghost'
                    onClick={() => {
                      setQuery('');
                      setSubmitted('');
                      setSelectedPath(null);
                    }}
                  >
                    {t('codable.libraryClear')}
                  </Button>
                )}
              </div>
            </form>

            <div className='flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
              <div className='flex flex-wrap items-center gap-1.5'>
                <span className='mr-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500'>
                  <Filter size={11} />
                  {t('codable.libraryType')}
                </span>
                {KINDS.map((k) => {
                  const active = kind === k.value;
                  const Icon = k.icon;
                  const count = k.value
                    ? kindCounts.get(k.value)
                    : results.length || undefined;
                  return (
                    <Button
                      key={k.value || 'all'}
                      type='button'
                      size='sm'
                      title={k.hint}
                      variant={active ? 'primary' : 'secondary'}
                      icon={<Icon size={13} />}
                      onClick={() => {
                        setKind(k.value);
                        setSelectedPath(null);
                      }}
                    >
                      {k.label}
                      {typeof count === 'number' && count > 0 && (
                        <Badge variant={active ? 'info' : 'default'} className='ml-1'>
                          {count}
                        </Badge>
                      )}
                    </Button>
                  );
                })}
              </div>

              <Toggle
                value={includeDrafts}
                onSave={(v) => {
                  setIncludeDrafts(v);
                  setSelectedPath(null);
                }}
                label={t('codable.libraryIncludeDrafts')}
              />
            </div>

            <div className='flex flex-wrap items-center gap-3 border-t border-gray-800/60 pt-3 text-xs text-gray-500'>
              <span className='inline-flex items-center gap-1.5'>
                {searchQ.isFetching
                  ? (
                    <>
                      <Loader2 size={12} className='animate-spin text-blue-400' />
                      {t('codable.libraryLoading')}
                    </>
                  )
                  : browsing
                  ? (
                    <>
                      <BookOpen size={12} className='text-gray-500' />
                      {t('codable.libraryBrowsing', { n: results.length })}
                    </>
                  )
                  : (
                    <>
                      <Search size={12} />
                      {t('codable.libraryResultsFor', { q: submitted, n: results.length })}
                    </>
                  )}
              </span>
              {searchQ.data?.lifecycle_filter && (
                <Badge variant='default'>lifecycle: {searchQ.data.lifecycle_filter}</Badge>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Split: list + reader */}
        <div className='grid grid-cols-1 gap-4 lg:grid-cols-[minmax(300px,380px)_1fr] lg:min-h-[62vh]'>
          <Card className='flex max-h-[70vh] flex-col lg:max-h-[calc(100vh-16rem)]'>
            <CardHeader>
              <span className='text-xs font-semibold uppercase tracking-wider text-gray-400'>
                {t('codable.libraryItems')}
              </span>
              <Badge variant='default'>{results.length}</Badge>
            </CardHeader>

            <div className='min-h-0 flex-1 overflow-y-auto'>
              {searchQ.isError && (
                <div className='m-3 flex items-start gap-2 rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-200'>
                  <AlertTriangle size={16} className='mt-0.5 shrink-0' />
                  <span>{(searchQ.error as Error)?.message ?? t('codable.librarySearchFailed')}</span>
                </div>
              )}

              {!searchQ.isFetching && results.length === 0 && (
                <EmptyState
                  className='py-14 px-4'
                  icon={<BookOpen size={32} className='mx-auto text-gray-600' />}
                  title={t('codable.libraryEmptyTitle')}
                  description={
                    searchQ.data?.note ?? t('codable.libraryEmptyDesc')
                  }
                />
              )}

              <ul className='space-y-1 p-2'>
                {results.map((hit) => {
                  const active = selectedPath === hit.path;
                  const meta = kindMeta(hit.kind);
                  const Icon = meta.Icon;
                  const folder = folderOf(hit.path);
                  return (
                    <li key={hit.path}>
                      <button
                        type='button'
                        onClick={() => setSelectedPath(hit.path)}
                        className={[
                          'group w-full rounded-xl border px-3 py-2.5 text-left transition-colors',
                          active
                            ? 'border-blue-500/30 bg-blue-500/10'
                            : 'border-transparent hover:border-gray-800 hover:bg-gray-800/40',
                        ].join(' ')}
                      >
                        <div className='flex items-start gap-2.5'>
                          <div
                            className={[
                              'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                              meta.iconBg,
                            ].join(' ')}
                          >
                            <Icon size={14} />
                          </div>
                          <div className='min-w-0 flex-1'>
                            <div className='truncate text-sm font-semibold text-gray-100 group-hover:text-white'>
                              {hit.title || hit.path}
                            </div>
                            {folder && (
                              <div className='mt-0.5 truncate text-[10px] font-medium uppercase tracking-wide text-gray-600'>
                                {folder}
                              </div>
                            )}
                            <div className='mt-1.5 flex flex-wrap items-center gap-1'>
                              <Badge variant={meta.variant}>{meta.label}</Badge>
                              {hit.lifecycle && hit.lifecycle !== 'active' && (
                                <Badge variant={kindMeta(hit.lifecycle).variant}>
                                  {hit.lifecycle}
                                </Badge>
                              )}
                              {hit.lifecycle === 'active' && (
                                <span className='inline-flex items-center gap-0.5 text-[10px] text-emerald-500/90'>
                                  <CheckCircle2 size={10} />
                                  active
                                </span>
                              )}
                            </div>
                            {hit.preview && (
                              <p className='mt-1.5 line-clamp-2 text-[11px] leading-relaxed text-gray-500'>
                                {hit.preview}
                              </p>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          </Card>

          {/* Reader */}
          <Card className='flex max-h-[70vh] flex-col lg:max-h-[calc(100vh-16rem)]'>
            {!selectedPath && (
              <div className='flex flex-1 items-center justify-center p-10'>
                <EmptyState
                  icon={BookOpen}
                  title={t('codable.libraryPickTitle')}
                  description={t('codable.libraryPickDesc')}
                />
              </div>
            )}

            {selectedPath && (
              <>
                <div className='flex items-start justify-between gap-3 border-b border-gray-800/70 px-5 py-4'>
                  <div className='min-w-0 flex items-start gap-3'>
                    <div
                      className={[
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                        selMeta.iconBg,
                      ].join(' ')}
                    >
                      <selMeta.Icon size={18} />
                    </div>
                    <div className='min-w-0'>
                      <h2 className='truncate text-base font-bold text-white'>
                        {readQ.data?.title || selected?.title || selectedPath}
                      </h2>
                      <Button
                        type='button'
                        variant='ghost'
                        size='sm'
                        className='mt-0.5 h-auto px-0 font-mono text-[11px] text-gray-500'
                        icon={<Copy size={11} />}
                        onClick={() => copyPath(selectedPath)}
                        title={t('codable.libraryCopyPath')}
                      >
                        <span className='truncate'>{selectedPath}</span>
                      </Button>
                      <div className='mt-2 flex flex-wrap gap-1'>
                        {selected?.kind && (
                          <Badge variant={selMeta.variant}>{selMeta.label}</Badge>
                        )}
                        {selected?.lifecycle && (
                          <Badge variant={kindMeta(selected.lifecycle).variant}>
                            {selected.lifecycle}
                          </Badge>
                        )}
                        {(selected?.tags ?? []).slice(0, 4).map((tag) => (
                          <Badge key={tag} variant='default'>
                            #{tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className='min-h-0 flex-1 overflow-y-auto px-5 py-5'>
                  {readQ.isLoading && (
                    <div className='flex items-center gap-2 text-sm text-gray-400'>
                      <Loader2 size={14} className='animate-spin text-blue-400' />
                      {t('codable.libraryLoadingContent')}
                    </div>
                  )}
                  {readQ.data?.error && (
                    <div className='rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-200'>
                      {readQ.data.error}
                    </div>
                  )}
                  {readQ.data?.ok && (
                    <article
                      className={[
                        'prose prose-invert prose-sm max-w-none',
                        'prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-white',
                        'prose-h1:text-xl prose-h2:text-lg prose-h3:text-base',
                        'prose-p:text-gray-300 prose-p:leading-relaxed',
                        'prose-a:text-blue-300 prose-a:no-underline hover:prose-a:underline',
                        'prose-strong:text-gray-100',
                        'prose-code:rounded prose-code:bg-gray-800 prose-code:px-1 prose-code:py-0.5 prose-code:text-blue-200 prose-code:before:content-none prose-code:after:content-none',
                        'prose-pre:rounded-xl prose-pre:border prose-pre:border-gray-800 prose-pre:bg-gray-950',
                        'prose-li:text-gray-300',
                        'prose-blockquote:border-blue-500/40 prose-blockquote:text-gray-400',
                        'prose-hr:border-gray-800',
                      ].join(' ')}
                    >
                      <Markdown>{readQ.data.content ?? ''}</Markdown>
                    </article>
                  )}
                </div>
              </>
            )}
          </Card>
        </div>

        <p className='text-center text-[11px] text-gray-600'>
          {t('codable.libraryFooter')}
        </p>
        </>
      )}
    </div>
  );
}

export default CodableLibrary;

/** Widget wrapper pro ModuleWorkspace záložku Knihovna. */
export function CodableLibraryWidget(
  _props: import('../../../widgets/types').WidgetRenderProps,
) {
  return <CodableLibrary embedded />;
}
